import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, brl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Loader2, Wallet, ClipboardList, UserCircle2, Car, MessageCircle, Printer, Target, Send } from "lucide-react";
import { WelcomeResultDialog } from "@/components/WelcomeResultDialog";
import { printAppPage } from "@/lib/print";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { METHOD_LABELS } from "@/components/PaymentDialog";
import { StatusBadge } from "@/components/StatusBadge";

const SRC = { google: "Google", indicacao: "Indicação", pos_venda: "Pós-venda", instagram: "Instagram", facebook: "Facebook", ja_cliente: "Já é cliente", outro: "Outro" };

function Kpi({ label, value, icon: Icon, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-card2-border bg-card2 p-4">
      <div className="flex items-center gap-2 text-[11px] font-mono-caps text-zinc-500 uppercase"><Icon className="h-3.5 w-3.5" /> {label}</div>
      <div className="font-display text-2xl font-black text-zinc-100 mt-1">{value}</div>
    </div>
  );
}

export default function CustomerSummary() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [inviting, setInviting] = useState(false);
  const { user } = useAuth();
  const resendInvite = async () => {
    setInviting(true);
    try { const { data } = await api.post(`/customers/${id}/invite`); setInvite(data); }
    catch (e) { toast.error(formatApiError(e)); } finally { setInviting(false); }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/customers/${id}/summary`);
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-green-brand" /></div>;
  if (!data) return <div className="text-zinc-400">Cliente não encontrado.</div>;

  const { customer, stats, vehicles, timeline, payments_by_order } = data;
  const phoneDigits = (customer?.phone || "").replace(/\D/g, "");
  const wa = phoneDigits ? (phoneDigits.length >= 12 ? phoneDigits : `55${phoneDigits}`) : "";

  return (
    <div className="space-y-5 animate-fade-up">
      <WelcomeResultDialog result={invite} onClose={() => setInvite(null)} shopName={user?.shop?.name} title="Convite reenviado" />
      <div className="flex items-center gap-2">
        <Link to="/owner/customers" className="text-zinc-400 hover:text-zinc-200 text-sm inline-flex items-center gap-1"><ArrowLeft className="h-4 w-4" /> Clientes</Link>
      </div>
      <div className="rounded-2xl border border-card2-border bg-card2 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] font-mono-caps text-zinc-500">Ficha do cliente</div>
            <h1 data-testid="crm-customer-name" className="font-display text-2xl sm:text-3xl font-black uppercase text-zinc-100 mt-1">{customer.name}</h1>
            <div className="mt-1 text-sm text-zinc-400 flex items-center gap-3 flex-wrap">
              <span>{customer.email}</span>
              {customer.phone && <span>· {customer.phone}</span>}
              {customer.source && <span className="inline-flex items-center gap-1 text-xs rounded-full border border-amber-brand/40 bg-amber-brand/10 text-amber-300 px-2 py-0.5 uppercase"><Target className="h-3 w-3" /> Como chegou: {SRC[customer.source] || customer.source}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {wa && (
              <a data-testid="crm-wa-btn" href={`https://wa.me/${wa}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold uppercase"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
            )}
            <Button data-testid="crm-resend-invite-btn" type="button" variant="outline" disabled={inviting} onClick={resendInvite} className="h-9 px-3 bg-zinc-900 border-zinc-800 text-zinc-200 hover:bg-zinc-800 text-xs font-bold uppercase">{inviting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Send className="h-3.5 w-3.5 mr-1" /> Reenviar convite</>}</Button>
            <Button data-testid="crm-print-btn" type="button" onClick={() => printAppPage(`/owner/customers/${id}/ficha`)} className="h-9 px-3 bg-zinc-100 text-zinc-900 hover:bg-white text-xs font-bold uppercase"><Printer className="h-3.5 w-3.5 mr-1" /> Imprimir</Button>
            <Link to={`/owner/customers/${id}/ficha`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-zinc-800 bg-zinc-900 text-zinc-200 hover:bg-zinc-800 text-xs font-bold uppercase"><Printer className="h-3.5 w-3.5" /> Ver ficha</Link>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi testid="crm-total" icon={Wallet} label="Total gasto" value={brl(stats.total_spent)} />
        <Kpi testid="crm-orders" icon={ClipboardList} label="Ordens" value={stats.orders} />
        <Kpi testid="crm-ticket" icon={Wallet} label="Ticket médio" value={brl(stats.avg_ticket)} />
        <Kpi testid="crm-last" icon={UserCircle2} label="Última visita" value={stats.last_visit ? new Date(stats.last_visit).toLocaleDateString("pt-BR") : "—"} />
      </div>

      {vehicles.length > 0 && (
        <div className="rounded-2xl border border-card2-border bg-card2 p-4">
          <div className="flex items-center gap-2 mb-3 text-xs font-mono-caps text-zinc-500 uppercase"><Car className="h-4 w-4" /> Veículos ({vehicles.length})</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {vehicles.map((v) => (
              <Link key={v.plate} to={`/owner/veiculo/${encodeURIComponent(v.plate)}`} data-testid={`crm-vehicle-${v.plate}`} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3 hover:bg-zinc-900 transition-colors">
                <div className="font-mono text-sm text-zinc-100">{v.plate}</div>
                <div className="text-xs text-zinc-400">{[v.make, v.model, [v.year, v.year_model].filter(Boolean).join("/")].filter(Boolean).join(" · ")}</div>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="rounded-2xl border border-card2-border bg-card2 p-4">
        <div className="flex items-center gap-2 mb-3 text-xs font-mono-caps text-zinc-500 uppercase"><ClipboardList className="h-4 w-4" /> Linha do tempo</div>
        {timeline.length === 0 ? <p className="text-sm text-zinc-500">Nenhuma ordem registrada.</p> : (
          <ol className="space-y-2">
            {timeline.map((o) => (
              <li key={o.id} data-testid={`crm-timeline-${o.id}`} className="flex items-start gap-3 p-2 rounded-lg hover:bg-zinc-900/50">
                <div className="h-9 w-9 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center shrink-0 text-xs font-bold text-zinc-300">
                  {o.order_number != null ? `#${o.order_number}` : "—"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/owner/ordens/${o.id}`} className="text-sm font-bold text-zinc-100 hover:text-green-neon">{[o.vehicle?.make, o.vehicle?.model].filter(Boolean).join(" ") || "OS"} · <span className="font-mono text-xs text-zinc-400">{o.vehicle?.plate || ""}</span></Link>
                    <StatusBadge status={o.status} short />
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    {new Date(o.created_at).toLocaleDateString("pt-BR")}
                    {o.budget_total != null && <> · <span className="text-green-neon font-semibold">{brl(o.budget_total)}</span></>}
                    {o.description && <> · {o.description.slice(0, 60)}{o.description.length > 60 ? "…" : ""}</>}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      {payments_by_order && payments_by_order.length > 0 && (
        <div data-testid="crm-payments" className="rounded-2xl border border-amber-brand/40 bg-amber-brand/5 p-4">
          <div className="flex items-center gap-2 mb-3 text-xs font-mono-caps text-amber-300 uppercase"><Wallet className="h-4 w-4" /> Formas de pagamento por OS (interno — não aparece para o cliente)</div>
          <ul className="divide-y divide-zinc-800">
            {payments_by_order.map((p) => (
              <li key={p.order_id} data-testid={`crm-payment-${p.order_id}`} className="py-2 flex items-start justify-between gap-2 text-sm">
                <div>
                  <Link to={`/owner/ordens/${p.order_id}`} className="font-bold text-zinc-100 hover:text-amber-200">OS #{p.order_number ?? "—"}</Link>
                  <div className="text-[11px] text-zinc-400">{new Date(p.created_at).toLocaleDateString("pt-BR")}</div>
                  <div className="text-xs text-zinc-300 mt-1">
                    {(p.payment.methods || []).map((m, i) => (
                      <span key={i} className="inline-block mr-3">{METHOD_LABELS[m.method] || m.method}: <span className="text-amber-200 font-semibold">{brl(m.amount)}</span></span>
                    ))}
                  </div>
                  {p.payment.internal_note && <div className="text-[11px] text-zinc-500 italic mt-0.5">“{p.payment.internal_note}”</div>}
                </div>
                <div className="text-amber-200 font-display font-black text-base shrink-0">{brl(p.payment.total || 0)}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
